/**
 * InfoModal.js
 * Info button + modal overlay explaining site navigation.
 * Mirrors hamburger menu positioning, animation, and interaction patterns.
 */

export class InfoModal {
    constructor(navigationController) {
        this.navigationController = navigationController;
        this.isOpen = false;

        this.createElements();
        this.attachEventListeners();
    }

    createElements() {
        // Info button (top-right, mirrors hamburger button style)
        this.button = document.createElement('button');
        this.button.className = 'circular-btn circular-btn-info';
        this.button.setAttribute('aria-label', 'Info');
        this.button.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="white"/>
            </svg>
        `;

        // Close button (same position, hidden initially)
        this.closeButton = document.createElement('button');
        this.closeButton.className = 'circular-btn circular-btn-info-close';
        this.closeButton.setAttribute('aria-label', 'Close info');
        this.closeButton.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="white"/>
            </svg>
        `;
        gsap.set(this.closeButton, { autoAlpha: 0 });

        // Modal overlay
        this.overlay = document.createElement('div');
        this.overlay.className = 'info-modal-overlay';

        // Modal content
        const content = document.createElement('div');
        content.className = 'info-modal-content';
        content.innerHTML = `
            <img src="/images/br-logo.svg" alt="BR" class="info-modal-logo" />
            <h2 class="info-modal-title">Burton Rast Photography</h2>
            <p class="info-modal-body">This contact sheet is easily navigated via the usual suspect mouse or keyboard means on desktop, though it's particularly fun on mobile.</p>
            <p class="info-modal-body">Pinch to zoom in and out of the sheet, individual photos and their detail views, swipe to visit adjacent images, or tap and hold for context menu shortcuts.</p>
            <p class="info-modal-body">No matter your chosen device, explore however you see fit, and enjoy my <strong>Unintentionally Moody™</strong> imagery.</p>
        `;

        this.overlay.appendChild(content);

        // Add to DOM
        document.body.appendChild(this.button);
        document.body.appendChild(this.closeButton);
        document.body.appendChild(this.overlay);
    }

    attachEventListeners() {
        this.button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.open();
        });

        this.closeButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.close();
        });

        // Click outside content to close
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.close();
            }
        });

        // Prevent content clicks from closing
        const content = this.overlay.querySelector('.info-modal-content');
        content.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }

    open() {
        if (this.isOpen) return;
        this.isOpen = true;

        // Close hamburger menu if open
        if (this.navigationController?.hamburgerMenu?.isOpen) {
            this.navigationController.hamburgerMenu.close();
        }

        document.body.style.overflow = 'hidden';

        // Fade info button out, close button in
        gsap.killTweensOf([this.button, this.closeButton, this.overlay]);
        gsap.to(this.button, { autoAlpha: 0, duration: 0.15, ease: 'power2.in' });
        gsap.to(this.closeButton, { autoAlpha: 1, duration: 0.2, ease: 'power2.out' });

        // Show overlay
        gsap.set(this.overlay, { display: 'flex' });
        gsap.to(this.overlay, { opacity: 1, duration: 0.3, ease: 'power2.out' });

        // Stagger content in
        const items = this.overlay.querySelectorAll('.info-modal-logo, .info-modal-title, .info-modal-content > .info-modal-body, .info-modal-section');
        gsap.fromTo(items,
            { opacity: 0, y: 10 },
            { opacity: 1, y: 0, duration: 0.3, stagger: 0.05, ease: 'power2.out', delay: 0.1 }
        );
    }

    close({ restoreButton = true } = {}) {
        if (!this.isOpen) return;
        this.isOpen = false;

        gsap.killTweensOf([this.button, this.closeButton, this.overlay]);

        // Fade close button out and only restore the info button when overview is actually visible again
        gsap.to(this.closeButton, { autoAlpha: 0, duration: 0.2, ease: 'power2.in' });
        if (restoreButton) {
            gsap.to(this.button, { autoAlpha: 1, duration: 0.2, ease: 'power2.out', delay: 0.1 });
        }

        // Hide overlay
        gsap.to(this.overlay, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.in',
            onComplete: () => {
                this.overlay.style.display = 'none';
                document.body.style.overflow = '';
            }
        });
    }

    hideButtonImmediately() {
        gsap.killTweensOf(this.button);
        gsap.to(this.button, { autoAlpha: 0, duration: 0.15, ease: 'power2.in' });
    }

    /** Called by NavigationController.setState — show only in OVERVIEW */
    onStateChange(newState) {
        if (this.isOpen && newState !== 'OVERVIEW') {
            this.close({ restoreButton: false });
        }

        gsap.killTweensOf(this.button);

        if (newState === 'OVERVIEW' && !this.isOpen && !this.navigationController?.isTransitioning) {
            gsap.to(this.button, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' });
        } else {
            gsap.to(this.button, { autoAlpha: 0, duration: 0.3, ease: 'power2.in' });
        }
    }

    dispose() {
        [this.button, this.closeButton, this.overlay].forEach(el => {
            if (el?.parentNode) el.parentNode.removeChild(el);
        });
    }
}
